import classNames from 'classnames';
import { ComponentChild, h, JSX } from 'preact';

import { Box } from './Box';
import { ArrowDownIcon, ArrowUpIcon } from './Icons';
import { NumberInput, NumberInputProps } from './NumberInput';
import { BaseRow, CellProps, Column, Table, TableClasses } from './Table';
import { TicketsTable, TicketsTableProps } from './TicketsTable';

type AnchorProps = JSX.HTMLAttributes<HTMLAnchorElement>;

export interface TicketTypeRow extends BaseRow {
    summary?: {
        ticketType: string;
        price: string;
        quantityInput: NumberInputProps;
        isExpanded: boolean;
    };
    details?: {
        table?: TicketsTableProps;
        addLink: AnchorProps;
    };
}

export interface TicketTypesTableProps {
    headers: {
        type: string;
        price: string;
        quantity: string;
    };
    rows: TicketTypeRow[];
    isExpandable: boolean;
}

// TODO simpler structure on mobile

export const TicketTypesTable = (props: TicketTypesTableProps): JSX.Element => {
    const classes: TableClasses<TicketTypeRow> = {
        table: 'text-sm md:text-base w-full',
        headerCell: 'hidden sm:table-cell text-sm text-neutral-500 font-normal py-4 text-left',
        bodyRow: '',
        getBodyRow: (row): string => classNames({
            'cursor-pointer': props.isExpandable && row.summary,
            'md:bg-neutral-100': row.summary?.isExpanded,
        }),
    };

    const columns: Column<TicketTypeRow>[] = [
        {
            header: props.headers.type,
            headerClass: 'w-full pl-0',
            render: (row): ComponentChild => {
                if (row.details) {
                    return (
                        <Box class='md:ml-12'>
                            {row.details.table && <TicketsTable {...row.details.table} />}
                            <Box class='my-4'>
                                <a class='text-primary-900 hover:opacity-90 cursor-pointer' {...row.details.addLink} />
                            </Box>
                        </Box>
                    );
                }

                return (
                    <div class='flex space-x-1 md:space-x-2 font-semibold'>
                        {props.isExpandable && (
                            row.summary.isExpanded ? <div class='w-6'><ArrowUpIcon /></div> : <div class='w-6'><ArrowDownIcon /></div>
                        )}
                        <span>{row.summary.ticketType}</span>
                    </div>
                );
            },
            getClass: (row): string => row.summary ? 'pl-0 sm:px-4 py-2 border-t' : '',
            getProps: (row): CellProps => {
                if (row.details) {
                    return { colSpan: columns.length };
                }
            },
        },
        {
            header: props.headers.price,
            headerClass: 'text-right',
            render: (row): ComponentChild => row.summary?.price,
            getClass: (): string => 'pl-4 py-2 border-t text-right whitespace-nowrap',
        },
        {
            header: props.headers.quantity,
            headerClass: 'text-center px-4 py-2',
            render: (row): ComponentChild => (
                <NumberInput {...row.summary?.quantityInput} />
            ),
            getClass: (): string => 'pl-4 pr-0 sm:px-4 py-2 border-t',
        },
    ];

    return <Table classes={classes} columns={columns} rows={props.rows} />;
};
